import React from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { produce } from "immer";
import { find, uniqueId } from "lodash-es";
import { m } from "motion/react";

import { FlowVersion, FlowVersionStatus } from "src/api/flowTypes";
import { Divider } from "src/base-components/Divider";
import { Button } from "src/design-system/Button";
import { Tooltip } from "src/design-system/Tooltip";
import { DraftPill, PublishedPill } from "src/flow/Pills";
import { AdditionalVersionSelector } from "src/flowContainer/RunFlowButton/RunFlowButton";
import { VersionSelector } from "src/flowContainer/RunFlowButton/VersionSelector";

const MAX_ADDITIONAL_VERSIONS = 2;

export const MultiversionConfig: React.FC<{
  version: FlowVersion;
  availableVersions: FlowVersion[];
  additionalVersions: AdditionalVersionSelector[];
  onAddAdditionalVersion: (value: AdditionalVersionSelector[]) => void;
}> = ({
  version,
  availableVersions,
  additionalVersions,
  onAddAdditionalVersion,
}) => {
  const thereAreParallelVersions = !!additionalVersions.length;

  const additionalVersionLimitReached =
    additionalVersions.length === MAX_ADDITIONAL_VERSIONS;

  const addMoreButtonDisabled =
    additionalVersionLimitReached || availableVersions.length === 0;

  const tooltipMsg = additionalVersionLimitReached
    ? `You can only run up to ${
        MAX_ADDITIONAL_VERSIONS + 1
      } versions in parallel`
    : "All the versions available for this flow are already selected";

  return (
    <>
      <FixedVersion version={version} />
      {thereAreParallelVersions && (
        <m.div
          animate={{
            opacity: 1,
            transition: { delay: 0.15 },
          }}
          className="flex flex-col gap-y-3"
          initial={{ opacity: 0 }}
        >
          <Divider />
          <p className="font-inter-medium-13px">Compare with</p>
          {additionalVersions.map((value, i) => (
            <VersionSelector
              key={value.selectorId}
              otherAvailableVersions={availableVersions}
              selectedVersion={value.version}
              onChange={(newValueId) => {
                const updatedAdditionalVersions = produce(
                  additionalVersions,
                  (draft) => {
                    const newValue = find(availableVersions, [
                      "id",
                      newValueId,
                    ]);
                    if (!newValue) return;
                    draft[i].version = newValue;
                  },
                );
                onAddAdditionalVersion(updatedAdditionalVersions);
              }}
              onDelete={() => {
                const updatedAdditionalVersions = produce(
                  additionalVersions,
                  (draft) => {
                    draft.splice(i, 1);
                  },
                );
                onAddAdditionalVersion(updatedAdditionalVersions);
              }}
            />
          ))}
        </m.div>
      )}
      <m.div
        className="flex items-center justify-between"
        transition={{ type: "tween" }}
        layout
      >
        <Tooltip
          disabled={!addMoreButtonDisabled}
          placement="bottom"
          title={tooltipMsg}
          asChild
        >
          <Button
            dataLoc="add-more-versions-button"
            isDisabled={addMoreButtonDisabled}
            iconLeft={faPlus}
            variant="secondary"
            onClick={() =>
              onAddAdditionalVersion([
                ...additionalVersions,
                { selectorId: uniqueId(), version: availableVersions[0] },
              ])
            }
          >
            Add more
          </Button>
        </Tooltip>
      </m.div>
    </>
  );
};

const FixedVersion: React.FC<{ version: FlowVersion }> = ({ version }) => {
  return (
    <div className="flex items-center gap-x-2">
      <div className="flex min-h-[32px] flex-1 items-center justify-between gap-x-3 rounded-lg border border-gray-200 py-0.5 pr-2 pl-3 font-inter-normal-12px">
        {version.name}

        {version.status === FlowVersionStatus.PUBLISHED ? (
          <PublishedPill />
        ) : (
          <DraftPill />
        )}
      </div>
    </div>
  );
};
